import BlockType from 'src/enums/blockType';
import EdgeLabel from '../../../../../enums/edgeLabel';
import EdgeType from '../../../../../enums/edgeType';
import { Block, Edge, Segment } from '../../../../../types/shared/blocksV3';
import { v4 as uuidv4 } from 'uuid';
import { toastError } from 'src/Toaster/Toast';
import { getOppositeEdge } from './edgeUtils';

function getDefaultDeckbox() {
	const deckId = uuidv4();

	const defaultDeckbox: Block = {
		id: deckId,
		type: BlockType.DECKBOX,
		rotation: 0,
		dimensions: {
			width: 120,
			depth: 120,
			height: 0,
		},
		displayDimensions: {
			width: 120,
			height: 120,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();

			const newEdge = {
				label: edge,
				id: edgeId,
				length: 120,
				attachedToParent: edge == EdgeLabel.TOP,
				segments: [
					{
						id: uuidv4(),
						edgeId,
						blockId: deckId,
						length: 120,
						type:
							edge === EdgeLabel.TOP
								? EdgeType.HOUSE_ATTACHED
								: EdgeType.RAILING,
					},
				],
			};

			return newEdge;
		}),
		parentBlock: {
			id: null,
			type: null,
			edgeId: null,
			segmentId: null,
		},
	};

	return defaultDeckbox;
}

export default getDefaultDeckbox;

export function getDefaultDeckboxForEdge({
	blocks,
	parentBlock,
	selectedBlockEdge,
}: {
	blocks: Block[];
	parentBlock: Block | null;
	selectedBlockEdge: Edge | undefined;
}) {
	if (!parentBlock || !selectedBlockEdge) return;

	const deckboxes = blocks.filter((blk) => blk.type == BlockType.DECKBOX);
	const landings = deckboxes.flatMap((dk) => dk.landings);
	const stairs = deckboxes.flatMap((dk) => dk.stairs);
	const bumpouts = deckboxes.flatMap((dk) => dk.bumpouts);

	const allBlocks = [...deckboxes, ...landings, ...stairs, ...bumpouts];

	const vacantSegmentIndex = selectedBlockEdge.segments.findIndex((Segment) =>
		allBlocks.every((blk) => blk?.parentBlock.segmentId != Segment.id)
	);

	if (vacantSegmentIndex < 0) {
		toastError({
			title: 'No space left',
			description: `Edge ${selectedBlockEdge.label} on ${parentBlock.label} does not have space for a new DeckBox`,
		});
		return;
	}

	const vacantSegment = selectedBlockEdge.segments[vacantSegmentIndex];

	const updatedOldSegment: Segment = {
		...vacantSegment,
		type: EdgeType.DECKING,
	};

	const defaultDeckbox = constructDeckbox({
		parentBlock,
		selectedBlockEdge,
		vacantSegment,
	});

	return { defaultDeckbox, updatedOldSegment };
}

export function getDefaultDeckboxForEdgeAndSegment({
	parentBlock,
	selectedBlockEdge,
	selectedBlockSegment,
}: {
	parentBlock: Block | null;
	selectedBlockEdge: Edge | undefined;
	selectedBlockSegment: Segment;
}) {
	if (!parentBlock || !selectedBlockEdge) return;

	const updatedOldSegment: Segment = {
		...selectedBlockSegment,
		type: EdgeType.DECKING,
	};

	const defaultDeckbox = constructDeckbox({
		parentBlock,
		selectedBlockEdge,
		vacantSegment: selectedBlockSegment,
	});

	return { defaultDeckbox, updatedOldSegment };
}

function constructDeckbox({
	parentBlock,
	selectedBlockEdge,
	vacantSegment,
}: {
	parentBlock: Block;
	selectedBlockEdge: Edge;
	vacantSegment: Segment;
}) {
	const inferedHeight =
		parentBlock.type == BlockType.STAIR
			? parentBlock.dimensions.endHeight || 0
			: parentBlock?.dimensions?.height || 0;

	const widthDimension =
		selectedBlockEdge.label == EdgeLabel.TOP ||
		selectedBlockEdge.label == EdgeLabel.BOTTOM
			? 'width'
			: 'height';

	const inferedWidth = vacantSegment.length;

	const depth = 60;

	const deckboxId = uuidv4();

	const defaultDeckbox: Block = {
		id: deckboxId,
		type: BlockType.DECKBOX,
		dimensions: {
			width: inferedWidth,
			depth,
			height: inferedHeight,
		},
		displayDimensions: {
			width: widthDimension == 'width' ? inferedWidth : depth,
			height: widthDimension == 'width' ? depth : inferedWidth,
		},
		parentBlock: {
			id: vacantSegment.blockId,
			type: parentBlock?.type,
			edgeId: selectedBlockEdge.id,
			segmentId: vacantSegment.id,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();

			const edgeLength =
				edge === selectedBlockEdge.label ||
				edge === getOppositeEdge(selectedBlockEdge.label)
					? inferedWidth
					: depth;

			const newEdge = {
				label: edge,
				id: edgeId,
				length: edgeLength,
				attachedToParent: edge === getOppositeEdge(selectedBlockEdge.label),
				segments: [
					{
						id: uuidv4(),
						edgeId,
						blockId: deckboxId,
						length: edgeLength,
						type:
							edge === getOppositeEdge(selectedBlockEdge.label)
								? EdgeType.DECKING
								: EdgeType.RAILING,
					},
				],
			};

			return newEdge;
		}),
	};

	return defaultDeckbox;
}
